import { useEffect, useRef } from "react";
import ElementLine, { nextTypeAfterEnter } from "./ElementLine";
import TypePicker from "./TypePicker";
import { SCENE_PRESETS } from "./helpers";

/**
 * Main editor pane (left side).
 *  - quick-insert preset bar
 *  - centered text column (Hollywood format)
 *  - the list of lines + inline TypePicker when user requested it
 *  - "+ Añadir línea" button
 *  - sticky selection action bar
 */
export default function EditorPane({
  elements,
  language,
  sceneNumbers,
  selectedIds,
  autoFocusId,
  autoFocusCursor,
  typePickerForId,
  characterMatrix = [],
  onUpdateElement,
  onChangeType,
  onEnter,
  onTab,
  onBackspaceEmpty,
  onArrowUp,
  onArrowDown,
  onToggleSelect,
  onDragSelectRange,
  onFocus,
  onRequestTypePicker,
  onPickType,
  onCancelTypePicker,
  onInsertAfter,
  onAddPreset,
  onOpenRewrite,
  onDeleteSelected,
  onCopySelected,
  onCutSelected,
  onClearSelection,
  selectedCount = 0,
}) {
  const presets = SCENE_PRESETS[language || "es"];
  const lastEl = elements[elements.length - 1];

  // ── Drag-to-select entre bloques ────────────────────────────────────────
  // Solución al bug: <textarea> no permite selección nativa arrastrando el
  // cursor entre varios bloques (se corta al cruzar un personaje). Aquí
  // detectamos el drag y, cuando el cursor cruza a un bloque distinto del
  // ancla, cambiamos a modo "selección de bloques enteros" (visualmente
  // idéntico a Shift+Click). Dentro de un mismo bloque, la selección nativa
  // de texto sigue funcionando como siempre.
  const dragRef = useRef({ anchorId: null, active: false, lastCurrentId: null });
  const linesContainerRef = useRef(null);

  useEffect(() => {
    // Extrae el id del bloque bajo el cursor (subiendo por el DOM hasta
    // encontrar el contenedor con id="sp-line-XXX").
    const findBlockIdAt = (clientX, clientY) => {
      let el = document.elementFromPoint(clientX, clientY);
      while (el && el !== document.body) {
        const id = el.id || "";
        if (id.startsWith("sp-line-")) return id.replace("sp-line-", "");
        el = el.parentElement;
      }
      return null;
    };

    const onMouseMove = (e) => {
      const d = dragRef.current;
      if (!d.anchorId) return;
      // Solo actuamos si el botón izquierdo sigue pulsado (buttons & 1).
      if (!(e.buttons & 1)) {
        d.anchorId = null;
        d.active = false;
        return;
      }
      const currentId = findBlockIdAt(e.clientX, e.clientY);
      if (!currentId || currentId === d.anchorId) return;
      // Cruzamos a otro bloque distinto: activamos modo drag-select.
      if (!d.active) {
        d.active = true;
        // Blur activo para matar la selección nativa dentro del textarea
        // del bloque ancla (visualmente se ve raro convivir).
        if (document.activeElement && document.activeElement.blur) {
          try { document.activeElement.blur(); } catch (_e) { /* noop */ }
        }
      }
      if (currentId !== d.lastCurrentId) {
        d.lastCurrentId = currentId;
        onDragSelectRange?.(d.anchorId, currentId);
      }
    };

    const onMouseUp = () => {
      // Reset — la selección queda visible; el usuario puede pulsar
      // Cmd/Ctrl+C, X, Backspace o Reescribir sobre ella.
      dragRef.current = { anchorId: null, active: false, lastCurrentId: null };
    };

    // Nota: escuchamos a nivel de document para no perder eventos cuando
    // el ratón sale por el margen del panel durante el drag.
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [onDragSelectRange]);

  const handleContainerMouseDown = (e) => {
    // Solo click izquierdo — no interferir con click derecho o auxiliar.
    if (e.button !== 0) return;
    // Localizar el bloque donde se pulsó.
    let node = e.target;
    while (node && node !== e.currentTarget) {
      const id = node.id || "";
      if (id.startsWith("sp-line-")) {
        dragRef.current = {
          anchorId: id.replace("sp-line-", ""),
          active: false,
          lastCurrentId: null,
        };
        return;
      }
      node = node.parentElement;
    }
    dragRef.current = { anchorId: null, active: false, lastCurrentId: null };
  };

  return (
    <div className="flex min-h-[600px] flex-1 flex-col overflow-hidden rounded-xl border border-stone-300/70 bg-white shadow-sm">
      {/* Quick insert bar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-stone-200 bg-[#FAFAF6] px-3 py-1.5">
        <span className="text-[10px] uppercase tracking-widest text-stone-500">Insertar escena:</span>
        {presets.map((preset) => (
          <button
            key={preset}
            onClick={() => onAddPreset(preset)}
            data-testid={`sp-preset-${preset.replace(/\s+/g, "_")}`}
            className="rounded-full border border-stone-300 bg-white px-2 py-0.5 text-[11px] font-mono text-stone-700 transition hover:border-[#26775E] hover:text-[#26775E]"
          >
            {preset}
          </button>
        ))}
        <span className="ml-3 text-[10px] uppercase tracking-widest text-stone-400">
          Tab = elegir formato · Shift+Tab = ciclar · Enter = nueva línea · 1-9 = atajo de tipo
        </span>
      </div>

      {/* Barra contextual de selección — aparece cuando hay 1+ bloques seleccionados */}
      {selectedCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2"
             data-testid="sp-selection-bar">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-900">
            ✓ {selectedCount} {selectedCount === 1 ? "bloque seleccionado" : "bloques seleccionados"}
          </span>
          <span className="text-[10px] text-amber-700">·</span>
          <span className="text-[10px] text-amber-700">Shift+Click rango · ⌘C/X/V copiar/cortar/pegar</span>
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            {onOpenRewrite && (
              <button
                onClick={onOpenRewrite}
                data-testid="sp-selection-rewrite"
                className="rounded-full bg-[#941620] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#2C2B29]">
                ✨ Reescribir con IA
              </button>
            )}
            {onCopySelected && (
              <button
                onClick={onCopySelected}
                data-testid="sp-selection-copy"
                title="Copiar bloques (⌘/Ctrl + C)"
                className="rounded-full border border-amber-400 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-900 transition hover:border-amber-700 hover:bg-amber-100">
                📋 Copiar
              </button>
            )}
            {onCutSelected && (
              <button
                onClick={onCutSelected}
                data-testid="sp-selection-cut"
                title="Cortar bloques (⌘/Ctrl + X)"
                className="rounded-full border border-amber-400 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-900 transition hover:border-amber-700 hover:bg-amber-100">
                ✂ Cortar
              </button>
            )}
            {onDeleteSelected && (
              <button
                onClick={onDeleteSelected}
                data-testid="sp-selection-delete"
                title="Borra los bloques seleccionados (⌘/Ctrl + Backspace)"
                className="rounded-full bg-stone-800 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-rose-700">
                🗑 Borrar
              </button>
            )}
            {onClearSelection && (
              <button
                onClick={onClearSelection}
                data-testid="sp-selection-clear"
                className="rounded-full border border-amber-300 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-amber-900 transition hover:border-amber-600">
                Limpiar
              </button>
            )}
          </div>
        </div>
      )}

      {/* Lines — centered column for proper Hollywood format margins */}
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-8" onMouseDown={handleContainerMouseDown} ref={linesContainerRef}>
        <div className="mx-auto w-full max-w-[68ch]">
          {elements.map((el, idx) => (
            <div key={el.id} id={`sp-line-${el.id}`}>
              <ElementLine
                element={el}
                sceneNumber={el.type === "scene_heading" ? sceneNumbers[el.id] : null}
                selected={selectedIds.has(el.id)}
                autoFocus={autoFocusId === el.id}
                autoFocusCursor={autoFocusId === el.id ? autoFocusCursor : "end"}
                characterMatrix={characterMatrix}
                onChange={(patch) => onUpdateElement(el.id, patch)}
                onChangeType={(type) => onChangeType(el.id, type)}
                onEnter={(cursorPos) => onEnter(el.id, cursorPos)}
                onTab={(dir) => onTab(el.id, dir)}
                onBackspaceEmpty={() => onBackspaceEmpty(el.id)}
                onArrowUp={() => onArrowUp(idx)}
                onArrowDown={() => onArrowDown(idx)}
                onToggleSelect={(shiftKey) => onToggleSelect(el.id, shiftKey)}
                onFocus={() => onFocus(el.id)}
                onRequestTypePicker={() => onRequestTypePicker(el.id)}
              />
              {typePickerForId === el.id && (
                <TypePicker
                  onPick={(t) => onPickType(el.id, t)}
                  onCancel={onCancelTypePicker}
                />
              )}
            </div>
          ))}
          <button
            onClick={() => onInsertAfter(lastEl.id, nextTypeAfterEnter(lastEl.type), "")}
            data-testid="sp-add-line"
            className="mt-4 w-full rounded border-2 border-dashed border-stone-300 py-2 text-[11px] uppercase tracking-widest text-stone-400 transition hover:border-[#26775E] hover:text-[#26775E]"
          >
            + Añadir línea
          </button>
        </div>
      </div>

    </div>
  );
}
